import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, AvatarLabelGroup, Badge, Button, Card, CardHeader, DotsVertical, Menu, Metric, Table, Ticket01, UserMinus01 } from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import TintedAlert from "../../ClassesShared/TintedAlert";
import DecisionModal from "../../ClassesShared/DecisionModal";
import initialsFor from "../../ClassesShared/initials";
import personColorProps from "../../ClassesShared/personColor";
import { formatRegisteredAt } from "../../ClassesShared/classLabels";

const STATE_BADGES = {
  waiting: { label: "Waiting", color: "gray" },
  promoted: { label: "Spot held", color: "blue" },
  claiming: { label: "Checking out", color: "blue" },
  claimed: { label: "Claimed", color: "success" },
  left: { label: "Removed", color: "gray" },
};

export const waitlistStateBadge = (entry) => STATE_BADGES[entry.state] || { label: entry.state, color: "gray" };

export const missedOffersLabel = (entry) => {
  const missed = entry.expired_promotions_count || 0;
  if (missed < 1 || ["claimed", "left"].includes(entry.state)) return null;
  return `Missed ${missed} ${missed === 1 ? "offer" : "offers"}`;
};

export const removalCopy = (entry) => ({
  title: `Remove ${entry.full_name} from the waitlist?`,
  description: entry.state === "promoted"
    ? `The spot held for ${entry.full_name} goes back to the class. If the waitlist is on and someone is waiting, the next person in line is offered it automatically. No email is sent.`
    : `${entry.full_name} loses their place in line. No email is sent.`,
});

const releaseNote = (releasedHeldSpot, offersNextInLine) => {
  if (!releasedHeldSpot) return "";
  if (offersNextInLine) return " The next person in line is offered the freed spot automatically.";
  return " The freed spot goes back to the class. No one on the waitlist is offered it automatically.";
};

export const removedMessage = (entry, releasedHeldSpot, offersNextInLine) => (
  `Removed ${entry.full_name} from the waitlist.${releaseNote(releasedHeldSpot, offersNextInLine)}`
);

export default function WaitlistTab({ waitlistPath, csrfToken, timeZone, onChange = () => {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [removedCount, setRemovedCount] = useState(0);
  const queueRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(waitlistPath, { params: { format: "json" } });
      setData(resp.data);
      setError(null);
    } catch (e) {
      setError("Could not load the waitlist.");
    } finally {
      setLoading(false);
    }
  }, [waitlistPath]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (removedCount > 0 && queueRef.current) queueRef.current.focus();
  }, [removedCount]);

  const act = async (entry, request, successMessage) => {
    setWorkingId(entry.id);
    try {
      const resp = await request();
      toastSuccess(successMessage(resp.data));
      await load();
      onChange();
      return true;
    } catch (e) {
      toastError(e.response?.data?.errors?.base?.[0] || "Action failed.");
      await load();
      return false;
    } finally {
      setWorkingId(null);
    }
  };

  const headers = { "X-CSRF-Token": csrfToken };
  const promote = (entry) => act(entry, () => axios.post(`${waitlistPath}/${entry.id}/promote`, {}, { headers }), () => `Holding a spot for ${entry.full_name}.`);
  const remove = async (entry) => {
    const removed = await act(entry, () => axios.delete(`${waitlistPath}/${entry.id}`, { headers }), (body) => removedMessage(entry, body?.releasedHeldSpot, body?.offersNextInLine));
    setRemoving(null);
    if (removed) setRemovedCount((count) => count + 1);
  };

  const canManage = Boolean(data?.canManage);
  const queueColumns = [
    { key: "position", header: "#", render: (entry) => entry.position || "—" },
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (entry) => <AvatarLabelGroup size="sm" initials={initialsFor(entry.full_name)} {...personColorProps(entry.full_name)} name={entry.full_name} supportingText={entry.email} />,
    },
    {
      key: "state",
      header: "Status",
      render: (entry) => {
        const badge = waitlistStateBadge(entry);
        const missed = missedOffersLabel(entry);
        return (
          <>
            <Badge color={badge.color} size="sm" dot>{badge.label}</Badge>
            {missed && <span className="d-block small text-muted mt-1">{missed}</span>}
          </>
        );
      },
    },
    { key: "joined", header: "Joined", render: (entry) => formatRegisteredAt(entry.created_at, timeZone) },
    { key: "expires", header: "Hold expires", render: (entry) => (["promoted", "claiming"].includes(entry.state) ? formatRegisteredAt(entry.promotion_expires_at, timeZone) : "—") },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      align: "right",
      render: (entry) => {
        const items = [
          entry.promotable && { label: "Offer Spot", description: data.offerUnavailableReason || undefined, icon: Ticket01, onClick: () => promote(entry), disabled: workingId !== null || Boolean(data.offerUnavailableReason) },
          entry.removable && { label: "Remove", icon: UserMinus01, destructive: true, onClick: () => setRemoving(entry), disabled: workingId !== null },
        ].filter(Boolean);
        if (!canManage || items.length === 0) return null;
        return (
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${entry.full_name}`} />}
            items={items}
          />
        );
      },
    },
  ];
  const historyColumns = queueColumns.filter((column) => !["position", "expires", "actions"].includes(column.key));

  if (error) return <Alert color="error" role="alert" description={error} />;

  return (
    <div className="d-flex flex-column" style={{ gap: 24 }}>
      {data && !data.waitlistEnabled && (
        <TintedAlert color="warning" title="The waitlist is off" description="Nobody is offered a spot automatically. Edit the class to turn it on and let people queue for freed spots." />
      )}
      <div className="row">
        <div className="col-md-4 mb-3 mb-md-0">
          <Metric className="h-100" label="On the waitlist" value={data ? data.queue.length : "—"} />
        </div>
        <div className="col-md-4 mb-3 mb-md-0">
          <Metric className="h-100" label="Open spots" value={data ? data.spotsRemaining : "—"} />
        </div>
        <div className="col-md-4">
          <Metric className="h-100" label="Claim window" value={data ? `${data.claimWindowHours}h` : "—"} chart={<span className="small classes-text-secondary">{data ? `Each held spot lasts ${data.claimWindowLabel}` : ""}</span>} />
        </div>
      </div>

      <div ref={queueRef} tabIndex={-1} className="classes-focus-target">
        <Card padding="none" header={<CardHeader title="In Line" supportingText="People are offered a freed spot in this order." />}>
          <Table className="classes-table"
            columns={queueColumns}
            data={data?.queue || []}
            loading={loading}
            getRowKey={(entry) => entry.id}
            stackBelow={640}
            empty="Nobody is on the waitlist."
          />
        </Card>
      </div>

      {removing && (
        <DecisionModal
          type="error"
          icon={UserMinus01}
          title={removalCopy(removing).title}
          description={removalCopy(removing).description}
          cancelLabel="Keep on Waitlist"
          confirmLabel={workingId !== null ? "Removing…" : "Remove from Waitlist"}
          working={workingId !== null}
          onConfirm={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      )}

      {data?.history?.length > 0 && (
        <Card padding="none" header={<CardHeader title="Claimed or Removed" />}>
          <Table className="classes-table" columns={historyColumns} data={data.history} getRowKey={(entry) => entry.id} stackBelow={640} />
        </Card>
      )}
    </div>
  );
}

WaitlistTab.propTypes = {
  waitlistPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  onChange: PropTypes.func,
};
